import { AccountingDashboardSnapshot } from '@/renderer/types/accounting';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Landmark,
  ReceiptText,
  TrendingUp,
  WalletCards,
} from 'lucide-react';
import {
  formatCompactCurrency,
  formatCurrencyValue,
} from './accountingDashboard';

interface AccountingDashboardKpiCardsProps {
  snapshot: AccountingDashboardSnapshot;
}

const KPI_ITEMS = [
  {
    key: 'openingCashBalance',
    label: 'Opening balance',
    icon: Landmark,
    hero: true,
    format: 'currency',
  },
  {
    key: 'totalIncome',
    label: 'Income',
    icon: ArrowUpCircle,
    hero: false,
    format: 'currency',
  },
  {
    key: 'totalExpenses',
    label: 'Expenses',
    icon: ArrowDownCircle,
    hero: false,
    format: 'currency',
  },
  {
    key: 'netIncome',
    label: 'Net income',
    icon: TrendingUp,
    hero: false,
    format: 'currency',
  },
  {
    key: 'totalTransactions',
    label: 'Transactions',
    icon: ReceiptText,
    hero: false,
    format: 'count',
  },
  {
    key: 'closingCashBalance',
    label: 'Closing balance',
    icon: WalletCards,
    hero: false,
    format: 'currency',
  },
] as const;

export function AccountingDashboardKpiCards({
  snapshot,
}: AccountingDashboardKpiCardsProps) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {KPI_ITEMS.map((item) => {
        const Icon = item.icon;
        const value = snapshot[item.key];

        return (
          <div
            key={item.key}
            className={[
              'rounded-[20px] border p-3.5 shadow-sm',
              item.hero
                ? 'border-transparent bg-gradient-to-br from-pos-primary via-pos-secondary to-pos-primary text-white'
                : 'border-gray-200 bg-white',
            ].join(' ')}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p
                  className={[
                    'text-xs font-medium',
                    item.hero ? 'text-white/80' : 'text-slate-500',
                  ].join(' ')}
                >
                  {item.label}
                </p>
                <p
                  className={[
                    'mt-1.5 text-[1.75rem] font-semibold tracking-tight',
                    item.hero ? 'text-white' : 'text-slate-900',
                  ].join(' ')}
                >
                  {item.format === 'currency'
                    ? formatCompactCurrency(value)
                    : value.toLocaleString()}
                </p>
                <p
                  className={[
                    'mt-1.5 text-[11px]',
                    item.hero ? 'text-white/75' : 'text-slate-500',
                  ].join(' ')}
                >
                  {item.format === 'currency'
                    ? formatCurrencyValue(value)
                    : `${snapshot.totalJournalLines.toLocaleString()} journal lines in range`}
                </p>
              </div>
              <div
                className={[
                  'flex h-10 w-10 items-center justify-center rounded-xl',
                  item.hero
                    ? 'bg-white/15 text-white'
                    : 'bg-pos-primary/10 text-pos-primary',
                ].join(' ')}
              >
                <Icon size={18} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
